import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Details, ErrorSummary } from 'govuk-react-jsx';
import { Input } from 'govuk-react-jsx/govuk/components/input';
import {
  getNumberOfChargepointsInstalled,
  setNumberOfChargepointsInstalled,
  getNumberOfChargepointsSocketsInstalled,
  setNumberOfChargepointsSocketsInstalled,
  getGrantTypeSelected,
  getSelectedGrantValue,
} from '../../features/application/applicationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { confirmChargepointSockets } from '../../firebase';
import { getSelectedLandlordClient } from '../../features/pmc/pmcJourneySlice';
import useTitle from '../../hooks/useTitle';

const ChargepointInstallation = () => {
  const dispatch = useDispatch();

  const history = useHistory();
  const selectedGrantObject = useSelector(getSelectedGrantValue);
  const grantDocumentId = useSelector(getGrantTypeSelected);
  const numberOfChargepointsRedux = useSelector(
    getNumberOfChargepointsInstalled
  );
  const numberOfChargepointsSocketRedux = useSelector(
    getNumberOfChargepointsSocketsInstalled
  );
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const currentUserData = useSelector(getSelectedLandlordClient);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  useTitle(
    'pmcApp.ChargepointInstallation.s12',
    'How many chargepoint sockets will be installed? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const [errorMsgCP, setErrorMsgCP] = useState('');
  const [errorMsgCPS, setErrorMsgCPS] = useState('');
  const [errorList, setErrorList] = useState([]);

  const lowerLimit = 1;
  const comUpperLimit = 100;
  const resUpperLimit = 200;
  const comProperties = 'commercial properties';
  const resProperties = 'residential properties';
  const errorListArr = [];
  const residentialErrorMsg = copyProvider.getCopy(
    'pmcApp.ChargepointInstallation.s10',
    'Value for chargepoint sockets must be between 1 and 200'
  );

  const commercialErrorMsg = copyProvider.getCopy(
    'pmcApp.ChargepointInstallation.s11',
    'Value for chargepoint sockets must be between 1 and 100'
  );

  const showResError = {
    children: residentialErrorMsg,
  };
  const showComError = {
    children: commercialErrorMsg,
  };

  const validateChargePointsResidential = () => {
    if (
      (selectedGrantObject.grantTypeShort === resProperties &&
        !numberOfChargepointsRedux) ||
      numberOfChargepointsRedux < lowerLimit ||
      numberOfChargepointsRedux > resUpperLimit
    ) {
      setErrorMsgCP(showResError);
      errorListArr.push({ ...showResError, href: '#chargepointInstallation' });
      return false;
    }
    return true;
  };

  const validateChargePointsCommercial = () => {
    if (
      (selectedGrantObject.grantTypeShort === comProperties &&
        !numberOfChargepointsRedux) ||
      numberOfChargepointsRedux < lowerLimit ||
      numberOfChargepointsRedux > comUpperLimit
    ) {
      setErrorMsgCP(showComError);
      errorListArr.push({ ...showComError, href: '#chargepointInstallation' });
      return false;
    }
    return true;
  };

  const validateChargePointSocketsResidential = () => {
    if (
      (selectedGrantObject.grantTypeShort === resProperties &&
        !numberOfChargepointsSocketRedux) ||
      numberOfChargepointsSocketRedux < lowerLimit ||
      numberOfChargepointsSocketRedux > resUpperLimit
    ) {
      setErrorMsgCPS(showResError);
      errorListArr.push({
        ...showResError,
        href: '#chargepointInstallationSocket',
      });
      return false;
    }
    return true;
  };

  const validateChargePointSocketsCommercial = () => {
    if (
      (selectedGrantObject.grantTypeShort === comProperties &&
        !numberOfChargepointsSocketRedux) ||
      numberOfChargepointsSocketRedux < lowerLimit ||
      numberOfChargepointsSocketRedux > comUpperLimit
    ) {
      setErrorMsgCPS(showComError);
      errorListArr.push({
        ...showComError,
        href: '#chargepointInstallationSocket',
      });
      return false;
    }
    return true;
  };

  const handleClick = () => {
    if (
      selectedGrantObject.grantTypeShort === comProperties
        ? validateChargePointsCommercial() &&
          validateChargePointSocketsCommercial()
        : validateChargePointsResidential() &&
          validateChargePointSocketsResidential()
    ) {
      confirmChargepointSockets({
        chargepointSockets: numberOfChargepointsSocketRedux,
        grantDocumentId,
      })
        .then(() => {
          history.push('/pmcApp/check-your-answers');
        })
        .catch(() => {
          history.push('/pmcApp/unable-to-start-new-application');
        });
    } else {
      setErrorList(errorListArr);
    }
  };

  // Store num value to store
  const handleNumOfChargepoints = (event) => {
    dispatch(setNumberOfChargepointsInstalled(event.target.value));
  };

  const handleNumOfChargepointsSockets = (event) => {
    dispatch(setNumberOfChargepointsSocketsInstalled(event.target.value));
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <div>
        <span className="govuk-caption-l">
          {currentUserData != null &&
            currentUserData.landlord_account_name != null &&
            currentUserData.landlord_account_name}
        </span>
        <h1 className="govuk-heading-xl">
          {copyProvider.getCopy(
            'pmcApp.ChargepointInstallation.s1',
            'How many chargepoint sockets will be installed at this property?'
          )}
        </h1>

        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ChargepointInstallation.s2',
            'The number of chargepoint sockets may be different from the number of chargepoints. A chargepoint may have a single chargepoint socket or 2 chargepoint sockets (a twin or double socket).'
          )}
        </p>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ChargepointInstallation.s3',
            'OZEV pays 75% of the cost of buying and installing an EV chargepoint socket, up to £350.'
          )}
        </p>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcApp.ChargepointInstallation.s4',
            'The grant amount is calculated on the number of chargepoint sockets installed.'
          )}
        </p>

        <Details
          className="app-images-container"
          summaryChildren={copyProvider.getCopy(
            'pmcApp.ChargepointInstallation.s5',
            "I don't know the number of chargepoint sockets"
          )}
        >
          <p className="govuk-body">
            {copyProvider.getCopy(
              'pmcApp.ChargepointInstallation.s6',
              'Contact your installer to confirm the number of chargepoint sockets that will be installed. It is important that you agree on the number of chargepoint sockets that will be installed.'
            )}
          </p>
        </Details>
        <div>
          <div className="govuk-body">
            <Input
              errorMessage={errorMsgCP || null}
              label={{
                children: copyProvider.getCopy(
                  'pmcApp.ChargepointInstallation.s7',
                  'Total number of chargepoints'
                ),
              }}
              id="chargepointInstallation"
              className="govuk-input govuk-input--width-3"
              name="chargepointInstallation"
              type="number"
              value={numberOfChargepointsRedux}
              onChange={(event) => handleNumOfChargepoints(event)}
            />
          </div>
          <div className="govuk-body">
            <Input
              errorMessage={errorMsgCPS || null}
              label={{
                children: copyProvider.getCopy(
                  'pmcApp.ChargepointInstallation.s8',
                  'Total number of chargepoint sockets'
                ),
              }}
              id="chargepointInstallationSocket"
              className="govuk-input govuk-input--width-3"
              name="chargepointInstallationSocket"
              type="number"
              value={numberOfChargepointsSocketRedux}
              onChange={(event) => handleNumOfChargepointsSockets(event)}
            />
          </div>
          <Button className="govuk-button" onClick={handleClick}>
            {copyProvider.getCopy(
              'pmcApp.ChargepointInstallation.s9',
              'Continue'
            )}
          </Button>
        </div>
      </div>
    </>
  );
  return pageModel;
};

export default ChargepointInstallation;
